import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { v4 as uuidv4 } from 'uuid';

// Import ReactFlow
import { 
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import FunctionSelector from './FunctionSelector';
import NodeFunctionSelector from './NodeFunctionSelector';

// Custom node and edge components
const CustomNode = ({ data, selected }) => {
  return (
    <>
      {/* Connection handles for ReactFlow */}
      <Handle
        type="target"
        position={Position.Left}
        id="target"
        style={{ background: '#1976d2', width: 8, height: 8 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source"
        style={{ background: '#1976d2', width: 8, height: 8 }}
      />
      
      <Card 
        variant="outlined"
        sx={{ 
          minWidth: 200,
          maxWidth: 250, 
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: selected 
            ? '0 0 0 2px #1976d2, 0 4px 10px rgba(0, 0, 0, 0.1)' 
            : '0 1px 5px rgba(0, 0, 0, 0.1)',
          border: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          },
          backgroundColor: 'white'
        }}
      >
        <Box sx={{ 
          p: 1.5, 
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: data.is_initial ? 'rgba(25, 118, 210, 0.05)' : 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: data.is_initial ? 600 : 500,
              color: data.is_initial ? 'primary.main' : 'text.primary',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {data.name}
            {data.is_initial && (
              <Box 
                component="span" 
                sx={{ 
                  ml: 1,
                  fontSize: '0.7rem',
                  fontWeight: 'normal',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: '4px',
                  px: 0.5,
                  py: 0.2
                }}
              >
                Initial
              </Box>
            )}
          </Typography>
          <Box>
            {data.onEdit && (
              <IconButton size="small" onClick={data.onEdit} title="Edit agent">
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {data.language}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 1, 
              fontSize: '0.8rem', 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {data.instructions}
          </Typography>
          <Box sx={{ 
            mt: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center' 
          }}>
            {data.tools && data.tools.length > 0 && (
              <Typography variant="caption" sx={{ 
                bgcolor: 'rgba(0, 0, 0, 0.05)', 
                px: 0.5, 
                py: 0.2, 
                borderRadius: 1,
                fontSize: '0.7rem'
              }}>
                {data.tools.length} function{data.tools.length !== 1 ? 's' : ''}
              </Typography>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              {!data.is_initial && data.onDelete && (
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={data.onDelete}
                  title="Delete agent"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {data.onAddTransition && (
                <IconButton 
                  size="small" 
                  color="primary" 
                  onClick={data.onAddTransition}
                  title="Add transition"
                >
                  <CallSplitIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </>
  );
};

// Node types definition for ReactFlow
const nodeTypes = {
  custom: CustomNode
};

/**
 * WorkflowBuilder main component
 */
const WorkflowBuilderComponent = ({ value, onChange, disabled, languageOptions, getVoiceOptions }) => {
  const [editingTab, setEditingTab] = useState(0);

  // Function to find language label
  const getLanguageLabel = (langCode) => {
    const option = languageOptions.find(l => l.value === langCode);
    return option ? option.label : langCode;
  };
  
  // Use ReactFlow hooks
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useReactFlow();
  
  // Track internal vs external updates
  const isInternalUpdate = useRef(false);
  const previousValueRef = useRef(null);
  
  // Convert workflow data structure to ReactFlow nodes and edges
  const mapWorkflowToReactFlow = (workflowData) => {
    if (!workflowData) return { nodes: [], edges: [] };
    
    // Extract nodes
    const nodes = workflowData.nodes.map((node, index) => {
      // Generate a position if none exists
      const position = node.position || {
        x: 50 + (index % 3) * 300,
        y: 50 + Math.floor(index / 3) * 200
      };
      
      return {
        id: node.id,
        type: 'custom',
        position,
        data: {
          ...node,
          language: getLanguageLabel(node.language),
          onEdit: () => openEditDialog(node),
          onDelete: !node.is_initial ? () => deleteNode(node.id) : undefined,
          onAddTransition: () => openTransitionDialog(node)
        },
        draggable: !disabled
      };
    });
    
    // Extract edges
    const edges = workflowData.edges.map(edge => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: 'source',
      targetHandle: 'target',
      label: edge.condition || '',
      labelStyle: { 
        fill: '#666', 
        fontWeight: 500, 
        fontSize: 12 
      },
      labelBgStyle: { 
        fill: 'rgba(255, 255, 255, 0.9)', 
        fillOpacity: 0.9
      },
      labelBgPadding: [5, 5],
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: '#1976d2',
      },
      animated: true,
      style: { 
        stroke: '#1976d2', 
        strokeWidth: 2
      }
    }));
    
    return { nodes, edges };
  };
  
  // Initialize workflow with default if no value provided
  const initialWorkflow = value || {
    nodes: [
      {
        id: uuidv4(),
        name: 'Initial Agent',
        instructions: 'You are a helpful voice assistant.',
        language: 'en-US',
        voice: 'en-US-JennyNeural',
        is_initial: true,
        initial_message: 'Hello, how can I help you today?',
        tools: []
      }
    ],
    edges: []
  };
  
  // Set up ReactFlow nodes and edges
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Map internal workflow model
  const [workflow, setWorkflow] = useState(initialWorkflow);
  
  // Initialize ReactFlow with workflow data
  useEffect(() => {
    const { nodes, edges } = mapWorkflowToReactFlow(workflow);
    setNodes(nodes);
    setEdges(edges);
  }, [workflow]);
  
  // When value prop changes from parent, update our internal state
  useEffect(() => {
    // Skip if this is the first render
    if (!previousValueRef.current) {
      previousValueRef.current = value;
      return;
    }

    // Skip if the value is the same object reference
    if (value === workflow) return;

    // Skip if value hasn't actually changed in content
    const valStr = JSON.stringify(value);
    const workflowStr = JSON.stringify(workflow);
    if (valStr === workflowStr) return;

    // Update workflow with new value
    setWorkflow(value || initialWorkflow);

    // Update our reference
    previousValueRef.current = value;
  }, [value]);

  // Update parent when workflow changes (but only for internal updates)
  useEffect(() => {
    // Skip first render
    if (!previousValueRef.current) return;

    // Skip if disabled
    if (disabled) return;

    // Skip if this isn't an internal update
    if (!isInternalUpdate.current) return;
    
    // Only notify parent if there's an actual change
    if (onChange) {
      // Reset the flag after using it
      isInternalUpdate.current = false;
      
      // Create a stable copy to prevent reference issues
      const stableCopy = JSON.parse(JSON.stringify(workflow));
      onChange(stableCopy);
    }
  }, [workflow, onChange, disabled]);
  
  // Wrapper for all workflow state updates to mark them as internal
  const updateWorkflow = useCallback((updater) => {
    isInternalUpdate.current = true;
    setWorkflow(updater);
  }, []);
  
  // State for the node edit dialog
  const [editDialog, setEditDialog] = useState({
    open: false,
    node: null
  });
  
  // State for the edge/transition dialog
  const [transitionDialog, setTransitionDialog] = useState({
    open: false,
    sourceNode: null,
    targetNode: null,
    condition: ''
  });

  // Add this state for the function dialog
  const [functionDialog, setFunctionDialog] = useState({
    open: false,
    nodeId: null
  });

  // Add this state for available functions
  const [availableFunctions, setAvailableFunctions] = useState([]);

  // Add function to fetch available functions
  const fetchAvailableFunctions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/functions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableFunctions(data);
      } else {
        console.error('Error fetching functions');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Use useEffect to fetch functions when the component mounts
  useEffect(() => {
    fetchAvailableFunctions();
  }, []);

  // Add a function to handle adding a selected function
  const addFunctionToNode = (func) => {
    if (!editDialog.node) return;
    
    // Convert the selected database function to the expected tool format
    const newTool = {
      name: func.name,
      description: func.description || 'No description',
      type: 'function',
      parameters: func.parameters || []
    };
    
    // Add to the current node's tools
    const currentTools = editDialog.node.tools || [];
    setEditDialog(prev => ({
      ...prev,
      node: {
        ...prev.node,
        tools: [...currentTools, newTool]
      }
    }));
    
    // Close the function dialog
    setFunctionDialog({
      open: false,
      nodeId: null
    });
  };
  
  // Add a new node to the workflow
  const addNode = () => {
    // Create a new node ID
    const newNodeId = uuidv4();
    
    // Get a reference to existing nodes for creating default transfers
    const existingNodes = workflow.nodes;
    const newNodeIndex = existingNodes.length + 1;
    
    // Calculate position for new node
    // Try to use ReactFlow utilities to find a good position
    let position = { x: 100, y: 100 };
    
    if (reactFlowInstance) {
      const center = reactFlowInstance.getViewport();
      position = {
        x: center.x,
        y: center.y
      };
    } else if (existingNodes.length > 0) {
      // Fall back to simple positioning
      const lastNode = existingNodes[existingNodes.length - 1];
      if (lastNode.position) {
        position = {
          x: lastNode.position.x + 300,
          y: lastNode.position.y
        };
      }
    }
    
    // Create default tools including transfer functions
    const defaultTools = [];
    
    // If there's at least one existing node (the initial node),
    // add a transfer function to it as a default tool
    if (existingNodes.length > 0) {
      // Find the initial node to create a transfer back to it
      const initialNode = existingNodes.find(node => node.is_initial);
      if (initialNode) {
        defaultTools.push({
          name: "transition_to_node", // Match the backend function name exactly
          description: `Transfer back to the ${initialNode.name} when appropriate`,
          type: 'function',
          parameters: [
            {
              name: 'context',
              description: 'The current conversation context',
              required: true
            },
            {
              name: 'node_id',
              description: 'The ID of the node to transition to',
              required: true,
              default: initialNode.id
            }
          ]
        });
      }
    }
    
    // Create the new node with default tools and initial_message field
    const newNode = {
      id: newNodeId,
      name: `Agent ${newNodeIndex}`,
      instructions: 'You are a helpful voice assistant that can transfer to other agents when appropriate.',
      language: 'en-US',
      voice: 'en-US-JennyNeural',
      is_initial: existingNodes.length === 0, // First node is initial
      initial_message: '', // Add initial_message field expected by backend
      tools: defaultTools,
      position // Add position information
    };
    
    updateWorkflow(prev => ({
      ...prev,
      nodes: [...prev.nodes, newNode]
    }));
    
    // If this is the first node, open the edit dialog
    if (existingNodes.length === 0) {
      setTimeout(() => {
        openEditDialog(newNode);
      }, 50);
    }
  };
  
  // Delete a node from the workflow
  const deleteNode = (nodeId) => {
    // Don't allow deleting the initial node
    if (workflow.nodes.find(n => n.id === nodeId)?.is_initial) {
      return;
    }
    
    // Remove the node and any edges connected to it
    updateWorkflow(prev => ({
      ...prev,
      nodes: prev.nodes.filter(node => node.id !== nodeId),
      edges: prev.edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId)
    }));
  };
  
  // Handle node position changes
  const onNodeDragStop = (event, node) => {
    updateWorkflow(prev => ({
      ...prev,
      nodes: prev.nodes.map(n => 
        n.id === node.id 
          ? { ...n, position: node.position } 
          : n
      )
    }));
  };
  
  // Open the node edit dialog
  const openEditDialog = (node) => {
    setEditDialog({
      open: true,
      node: { ...node }
    });
  };
  
  // Close the node edit dialog
  const closeEditDialog = () => {
    setEditDialog({
      open: false,
      node: null
    });
  };
  
  // Save changes from the edit dialog
  const saveNodeEdit = () => {
    if (!editDialog.node) return;
    
    updateWorkflow(prev => ({
      ...prev,
      nodes: prev.nodes.map(node => 
        node.id === editDialog.node.id ? editDialog.node : node
      )
    }));
    
    closeEditDialog();
  };
  
  // Handle changes in the edit dialog form
  const handleEditDialogChange = (field) => (event) => {
    setEditDialog(prev => ({
      ...prev,
      node: {
        ...prev.node,
        [field]: event.target.value
      }
    }));
  };
  
  // Open the transition dialog to add an edge
  const openTransitionDialog = (sourceNode) => {
    setTransitionDialog({
      open: true,
      sourceNode,
      targetNode: null,
      condition: ''
    });
  };
  
  // Close the transition dialog
  const closeTransitionDialog = () => {
    setTransitionDialog({
      open: false,
      sourceNode: null,
      targetNode: null,
      condition: ''
    });
  };
  
  // Handle changes in the transition dialog
  const handleTransitionDialogChange = (field) => (event) => {
    setTransitionDialog(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  
  // Add a transition between nodes
  const addTransition = () => {
    if (!transitionDialog.sourceNode || !transitionDialog.targetNode) return;
    
    try {
      // Create a new edge with the source and target IDs
      const newEdge = {
        id: uuidv4(),
        source: transitionDialog.sourceNode.id,
        target: transitionDialog.targetNode,
        sourceHandle: 'source',
        targetHandle: 'target',
        condition: transitionDialog.condition
      };
      
      // Check if edge already exists before updating state
      const edgeExists = workflow.edges.some(
        e => e.source === newEdge.source && e.target === newEdge.target
      );
      
      // First close the dialog to prevent UI issues
      closeTransitionDialog();
      
      // Only update if this is a new edge
      if (!edgeExists) {
        // Debounce the update to avoid rapid state changes
        setTimeout(() => {
          updateWorkflow(prev => ({
            ...prev,
            edges: [...prev.edges, newEdge]
          }));
        }, 0);
      }
    } catch (error) {
      console.error("Error adding transition:", error);
    }
  };
  
  // Delete an edge/transition
  const onEdgeDelete = (edge) => {
    updateWorkflow(prev => ({
      ...prev,
      edges: prev.edges.filter(e => e.id !== edge.id)
    }));
  };
  
  // Auto-arrange nodes in a tree layout
  const autoLayout = () => {
    const nodes = [...workflow.nodes];
    
    // Find initial node
    const initialNode = nodes.find(n => n.is_initial);
    if (!initialNode) return;
    
    // Basic tree layout
    const levels = {};
    const visited = new Set();
    
    // Helper to get node level from root
    const getNodeLevel = (nodeId, level = 0) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      
      if (!levels[level]) levels[level] = [];
      levels[level].push(nodeId);
      
      // Get all outgoing edges
      const outgoingEdges = workflow.edges.filter(edge => edge.source === nodeId);
      outgoingEdges.forEach(edge => {
        getNodeLevel(edge.target, level + 1);
      });
    };
    
    // Build the level tree starting from the initial node
    getNodeLevel(initialNode.id);
    
    // Position nodes based on their level
    const LEVEL_HORIZONTAL_SPACING = 300;
    const LEVEL_VERTICAL_SPACING = 150;
    
    // Arrange nodes by level
    Object.keys(levels).forEach(level => {
      const nodesInLevel = levels[level];
      const levelInt = parseInt(level);
      
      nodesInLevel.forEach((nodeId, index) => {
        const node = nodes.find(n => n.id === nodeId);
        if (node) {
          node.position = {
            x: 50 + levelInt * LEVEL_HORIZONTAL_SPACING,
            y: 50 + index * LEVEL_VERTICAL_SPACING
          };
        }
      });
    });
    
    // Handle any nodes not visited (not connected to initial node)
    const unvisitedNodes = nodes.filter(node => !visited.has(node.id));
    unvisitedNodes.forEach((node, index) => {
      node.position = {
        x: 50,
        y: 50 + (Object.keys(levels).length * LEVEL_VERTICAL_SPACING) + (index * LEVEL_VERTICAL_SPACING)
      };
    });
    
    // Update workflow with new node positions
    updateWorkflow(prev => ({
      ...prev,
      nodes
    }));
  };
  
  // Extract data from ReactFlow format to our internal format
  const extractWorkflowFromReactFlow = useCallback(() => {
    const updatedNodes = nodes.map(node => {
      // Extract only the data we need from ReactFlow nodes
      const { id, position, data } = node;
      return {
        id,
        position,
        name: data.name,
        instructions: data.instructions,
        language: data.language,
        voice: data.voice,
        is_initial: data.is_initial,
        initial_message: data.initial_message,
        tools: data.tools
      };
    });
    
    const updatedEdges = edges.map(edge => {
      // Extract edge data
      const { id, source, target, label, sourceHandle, targetHandle } = edge;
      return {
        id,
        source,
        target,
        sourceHandle,
        targetHandle,
        condition: label
      };
    });
    
    return {
      nodes: updatedNodes,
      edges: updatedEdges
    };
  }, [nodes, edges]);
  
  // Handle connection between nodes
  const onConnect = useCallback((params) => {
    // Create a new edge
    const newEdge = {
      id: uuidv4(),
      source: params.source,
      target: params.target,
      sourceHandle: params.sourceHandle || 'source',
      targetHandle: params.targetHandle || 'target',
      condition: ''
    };
    
    // Update workflow with new edge
    updateWorkflow(prev => ({
      ...prev,
      edges: [...prev.edges, newEdge]
    }));
  }, [updateWorkflow]);

  const handleTabChange = (event, newValue) => {
    setEditingTab(newValue);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Workflow Builder</Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<SettingsIcon />}
                  onClick={autoLayout}
                  disabled={disabled || workflow.nodes.length < 2}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Auto Arrange
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addNode}
                  disabled={disabled}
                  size="small"
                >
                  Add Agent
                </Button>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create an agent workflow by defining multiple agents that can transfer control to each other.
              Drag agents to position them, and connect them to define transitions.
            </Typography>
            
            {/* ReactFlow canvas */}
            <Box
              ref={reactFlowWrapper}
              sx={{
                height: 600,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                position: 'relative'
              }}
            >
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDragStop={onNodeDragStop}
                onEdgeClick={(event, edge) => onEdgeDelete(edge)}
                nodeTypes={nodeTypes}
                fitView
                minZoom={0.2}
                maxZoom={1.5}
                defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                nodesDraggable={!disabled}
                nodesConnectable={!disabled}
                elementsSelectable={!disabled}
                zoomOnDoubleClick={!disabled}
                panOnDrag={!disabled}
                deleteKeyCode="Delete"
                selectionKeyCode="Shift"
                multiSelectionKeyCode="Control"
                connectionLineType="bezier"
                connectionLineStyle={{ stroke: '#1976d2', strokeWidth: 2 }}
                snapToGrid={true}
                snapGrid={[15, 15]}
                connectOnClick={false}
                edgeUpdaterRadius={100}
                defaultEdgeOptions={{
                  type: 'smoothstep',
                  animated: true,
                  style: { stroke: '#1976d2', strokeWidth: 2 }
                }}
              >
                <Background color="#aaa" gap={16} />
                <Controls />
                <MiniMap 
                  nodeStrokeWidth={3}
                  zoomable
                  pannable
                  style={{
                    height: 120,
                    width: 200
                  }}
                />
                
                {/* Empty state when no nodes */}
                {workflow.nodes.length === 0 && (
                  <Panel position="center">
                    <Box sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: 2,
                      maxWidth: 400
                    }}>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        No agents in this workflow. Add your first agent to get started.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addNode}
                        disabled={disabled}
                      >
                        Add First Agent
                      </Button>
                    </Box>
                  </Panel>
                )}
              </ReactFlow>
            </Box>
          </Paper>
        </Grid>
        
        {/* New Debug Panel */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Workflow Debug</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current workflow structure representation:
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(0, 0, 0, 0.04)', 
              borderRadius: 1, 
              overflow: 'auto',
              maxHeight: 'calc(600px - 100px)',
              fontFamily: 'monospace'
            }}>
              {/* Agent Summary */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Agents:</Typography>
              {workflow.nodes.map((node, index) => (
                <Box key={node.id} sx={{ mb: 2, pl: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {index + 1}. {node.name} {node.is_initial ? '(Initial)' : ''}
                  </Typography>
                  <Box component="pre" sx={{ 
                    pl: 2, 
                    fontSize: '0.7rem', 
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    ID: {node.id}
                    Instructions: {node.instructions.substring(0, 100)}{node.instructions.length > 100 ? '...' : ''}
                    Language: {node.language}
                    Voice: {node.voice}
                    Position: x={Math.round(node.position?.x || 0)}, y={Math.round(node.position?.y || 0)}
                    Functions: {node.tools?.length || 0}
                  </Box>
                </Box>
              ))}
              
              {/* Transition Summary */}
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Transitions:</Typography>
              {workflow.edges.length === 0 ? (
                <Typography variant="body2" sx={{ pl: 2, fontStyle: 'italic' }}>
                  No transitions defined
                </Typography>
              ) : (
                workflow.edges.map(edge => {
                  const sourceNode = workflow.nodes.find(n => n.id === edge.source);
                  const targetNode = workflow.nodes.find(n => n.id === edge.target);
                  return (
                    <Box key={edge.id} sx={{ mb: 1, pl: 2 }}>
                      <Typography variant="body2">
                        {sourceNode?.name || edge.source} → {targetNode?.name || edge.target}
                        {edge.condition && (
                          <Typography component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.85rem' }}>
                            {' '}({edge.condition})
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  );
                })
              )}
              
              {/* Raw JSON (collapsible) */}
              <Accordion sx={{ mt: 3, backgroundColor: 'transparent', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Raw JSON Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="pre" sx={{ 
                    fontSize: '0.7rem', 
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {JSON.stringify(workflow, null, 2)}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Node Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={closeEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialog.node ? 'Edit Node' : 'Add Node'}
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={editingTab}
            onChange={handleTabChange}
            sx={{ mb: 2 }}
          >
            <Tab label="Basic" />
            <Tab label="Advanced" />
          </Tabs>
          
          {editingTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Agent Name"
                  value={editDialog.node?.name || ''}
                  onChange={handleEditDialogChange('name')}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Instructions"
                  value={editDialog.node?.instructions || ''}
                  onChange={handleEditDialogChange('instructions')}
                  multiline
                  rows={4}
                  fullWidth
                  required
                  helperText="Define the agent's behavior, personality, and purpose"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Initial Message"
                  value={editDialog.node?.initial_message || ''}
                  onChange={handleEditDialogChange('initial_message')}
                  fullWidth
                  helperText="Message to say when this agent first takes control (optional)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={editDialog.node?.language || ''}
                    onChange={(e) => {
                      handleEditDialogChange('language')(e);
                      // Reset voice when language changes
                      setEditDialog(prev => ({
                        ...prev,
                        node: {
                          ...prev.node,
                          language: e.target.value,
                          voice: ''
                        }
                      }));
                    }}
                    label="Language"
                  >
                    {languageOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Voice</InputLabel>
                  <Select
                    value={editDialog.node?.voice || ''}
                    onChange={handleEditDialogChange('voice')}
                    label="Voice"
                  >
                    {editDialog.node?.language && getVoiceOptions(editDialog.node.language).map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Available Function Tools</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Functions that this agent can use during conversations. These define the agent's capabilities.
                </Typography>
                
                {/* Function tools list */}
                <Box sx={{ mb: 2 }}>
                  {(editDialog.node?.tools || []).map((tool, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1,
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {tool.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tool.description || 'No description'}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => {
                          // Remove this tool
                          setEditDialog(prev => ({
                            ...prev,
                            node: {
                              ...prev.node,
                              tools: prev.node.tools.filter((_, i) => i !== index)
                            }
                          }));
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  
                  {/* Tool adding section - MODIFIED */}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => {
                      // Open a dialog to choose from available functions
                      setFunctionDialog({
                        open: true,
                        nodeId: editDialog.node?.id
                      });
                    }}
                    sx={{ mt: 1 }}
                  >
                    Add Function Tool
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
          
          {editingTab === 1 && editDialog.node && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Node Functions
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Add custom functions that this node can access. Functions allow the agent to perform external actions like making API requests.
              </Typography>
              
              <NodeFunctionSelector 
                nodeId={editDialog.node.id} 
                disabled={disabled}
                onFunctionCountChange={(count) => {
                  updateWorkflow(prev => ({
                    ...prev,
                    nodes: prev.nodes.map(node => 
                      node.id === editDialog.node.id ? { ...node, data: { ...node.data, function_count: count } } : node
                    )
                  }));
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button onClick={saveNodeEdit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Transition Dialog */}
      <Dialog open={transitionDialog.open} onClose={closeTransitionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add Transition
          {transitionDialog.sourceNode && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              From: {transitionDialog.sourceNode.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Transitions allow this agent to transfer control to another agent based on specific conditions.
              Multiple transitions can be defined for a single agent, similar to how agent transfers work in workflow systems.
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Target Agent</InputLabel>
              <Select
                value={transitionDialog.targetNode || ''}
                onChange={handleTransitionDialogChange('targetNode')}
                label="Target Agent"
              >
                {workflow.nodes
                  .filter(node => node.id !== transitionDialog.sourceNode?.id)
                  .map(node => (
                    <MenuItem key={node.id} value={node.id}>
                      {node.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <TextField
              label="Condition for Transfer"
              placeholder="e.g., When user asks about billing, When a reservation is completed"
              value={transitionDialog.condition || ''}
              onChange={handleTransitionDialogChange('condition')}
              fullWidth
              multiline
              rows={3}
              helperText="Describe the specific condition that triggers this agent transfer (for documentation and clarity)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTransitionDialog}>Cancel</Button>
          <Button 
            onClick={addTransition} 
            variant="contained" 
            color="primary"
            disabled={!transitionDialog.targetNode}
          >
            Add Transition
          </Button>
        </DialogActions>
      </Dialog>

      {/* Function Selection Dialog */}
      <Dialog
        open={functionDialog.open}
        onClose={() => setFunctionDialog({ open: false, nodeId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Function</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a function to add to this agent:
          </Typography>
          
          {availableFunctions.length === 0 ? (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
              No functions available. Create functions first.
            </Typography>
          ) : (
            <List>
              {availableFunctions.map(func => (
                <ListItem 
                  key={func.id} 
                  button 
                  onClick={() => addFunctionToNode(func)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemText
                    primary={func.name}
                    secondary={func.description}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFunctionDialog({ open: false, nodeId: null })}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Wrap with ReactFlowProvider
const WorkflowBuilder = (props) => (
  <ReactFlowProvider>
    <WorkflowBuilderComponent {...props} />
  </ReactFlowProvider>
);

export default WorkflowBuilder; 